import axios from 'axios';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const USER_CONFIG_COLLECTION = 'userConfig';
const EXCHANGE_RATE_API = 'https://api.exchangerate-api.com/v4/latest/USD';

// Supported currencies with their symbols and names
export const SUPPORTED_CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', code: 'USD' },
  EUR: { symbol: '€', name: 'Euro', code: 'EUR' },
  GBP: { symbol: '£', name: 'British Pound', code: 'GBP' },
  JPY: { symbol: '¥', name: 'Japanese Yen', code: 'JPY' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', code: 'CAD' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', code: 'AUD' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', code: 'CHF' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', code: 'CNY' },
  KRW: { symbol: '₩', name: 'South Korean Won', code: 'KRW' },
  THB: { symbol: '฿', name: 'Thai Baht', code: 'THB' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', code: 'SGD' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', code: 'MYR' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', code: 'IDR' },
  PHP: { symbol: '₱', name: 'Philippine Peso', code: 'PHP' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', code: 'VND' }
};

// Cache for exchange rates
let exchangeRates = null;
let lastFetchTime = 0;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export const currencyService = {
  // Get user's currency preference
  async getUserCurrency(userId) {
    try {
      const docRef = doc(db, USER_CONFIG_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data().currency || 'USD';
      }
      
      // Default to USD if no preference set
      return 'USD';
    } catch (error) {
      console.error('Error getting user currency:', error);
      return 'USD';
    }
  },

  // Set user's currency preference
  async setUserCurrency(userId, currency) {
    try {
      const docRef = doc(db, USER_CONFIG_COLLECTION, userId);
      await setDoc(docRef, { currency }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error setting user currency:', error);
      return false;
    }
  },

  // Get exchange rates from API
  async getExchangeRates() {
    const now = Date.now();
    
    // Return cached rates if still valid
    if (exchangeRates && (now - lastFetchTime) < CACHE_DURATION) {
      return exchangeRates;
    }

    try {
      const response = await axios.get(EXCHANGE_RATE_API);
      exchangeRates = response.data.rates;
      lastFetchTime = now;
      return exchangeRates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Return cached rates if available, otherwise return null
      return exchangeRates;
    }
  },

  // Convert price from one currency to another
  async convertCurrency(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    try {
      const rates = await this.getExchangeRates();
      if (!rates) {
        return amount; // Return original amount if conversion fails
      }

      // Convert to USD first (base currency)
      const usdAmount = fromCurrency === 'USD' ? amount : amount / rates[fromCurrency];
      
      // Convert from USD to target currency
      const convertedAmount = toCurrency === 'USD' ? usdAmount : usdAmount * rates[toCurrency];
      
      return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount; // Return original amount if conversion fails
    }
  },

  // Format price with currency symbol
  formatPrice(amount, currency) {
    const currencyInfo = SUPPORTED_CURRENCIES[currency];
    if (!currencyInfo) {
      return `${amount} ${currency}`;
    }

    // Round to whole numbers and format with locale-specific number formatting
    const roundedAmount = Math.round(amount);
    
    // Format with locale-specific number formatting (no decimal places)
    const formattedNumber = roundedAmount.toLocaleString();
    
    return `${currencyInfo.symbol}${formattedNumber}`;
  },

  // Get currency symbol
  getCurrencySymbol(currency) {
    return SUPPORTED_CURRENCIES[currency]?.symbol || currency;
  },

  // Get currency name
  getCurrencyName(currency) {
    return SUPPORTED_CURRENCIES[currency]?.name || currency;
  }
}; 